import React, { useCallback } from 'react';
import { Column } from 'primereact/column';
import { DataTable, DataTableRowClickEvent, DataTableSortEvent } from 'primereact/datatable';
import { Skeleton } from 'primereact/skeleton';
import { useHistory } from 'react-router-dom';
import { EpdLinks } from 'routes/EpdRoutes';
import { EPDCopyGroupStatus, EPDStatusDisplayName } from 'services/EpdClient';
import { DashboardItemType, DashboardListItemModel } from 'types/dashboard/DashboardListItemModel';
import { dateToIsoString } from 'util/utils';
import styled from 'styled-components';
import { CardStatus } from '../styles';
import { EPDGroupCardDashboardStatus } from 'styles/v2/Styles.styled';
import { useTranslation } from 'react-i18next';
import Tooltip from 'components/controls/Tooltip';
import { ReactComponent as ArrowUpSelectedIcon } from 'images/v2/icons/svg/fill/svg/arrow-up-selected.svg';
import { ReactComponent as ArrowDownSelectedIcon } from 'images/v2/icons/svg/fill/svg/arrow-down-selected.svg';
import { ReactComponent as ArrowUpDownDefaultIcon } from 'images/v2/icons/svg/fill/svg/arrow-up-down-default.svg';

export type TableRow = {
  id: string;
  itemType: DashboardItemType;
  registrationNumber: string;
  name: string;
  epdStatus?: EPDStatusDisplayName;
  groupStatus?: EPDCopyGroupStatus;
  versionDate: string;
  validityDate: string;
  link: string;
};

export function toTableRow(item: DashboardListItemModel): TableRow {
  const link =
    item.type === DashboardItemType.CopyGroup
      ? EpdLinks.editCopyEpd(item.id)
      : item.type === DashboardItemType.DynamicFormDocument
        ? `/dynamic-forms/${item.id}`
        : EpdLinks.epd(item.id);

  return {
    id: item.id,
    itemType: item.type,
    registrationNumber: item.registrationNumber ?? '-',
    name: item.name ?? '-',
    epdStatus: item.statusDisplayName,
    groupStatus: item.groupStatus,
    versionDate: dateToIsoString(item.versionDate) ?? '-',
    validityDate: dateToIsoString(item.validityDate) ?? '-',
    link,
  };
}

type Props = {
  pagedCards: DashboardListItemModel[];
  loading?: boolean;
  sortField?: string | undefined;
  sortOrder?: 1 | -1 | null;
  onSort?: (field: string | undefined, order: 1 | -1 | null) => void;
};

const SKELETON_ROWS = Array.from({ length: 9 }, (_, index) => ({ id: String(index) }));

const DashboardListView: React.FC<Props> = ({ pagedCards, loading, sortField, sortOrder, onSort }) => {
  const history = useHistory();
  const { t } = useTranslation();

  const rows: TableRow[] = pagedCards.map(toTableRow);

  const handleSort = useCallback(
    (event: DataTableSortEvent) => {
      if (loading) {
        return;
      }
      onSort?.(event.sortField || undefined, event.sortOrder || null);
    },
    [loading, onSort]
  );

  const columnHeader = useCallback(
    (label: string, field: string) => (
      <ColumnHeaderWrapper>
        <span>{label}</span>
        {sortField === field && sortOrder === 1 ? (
          <SortIcon $active as={ArrowUpSelectedIcon} />
        ) : sortField === field && sortOrder === -1 ? (
          <SortIcon $active as={ArrowDownSelectedIcon} />
        ) : (
          <SortIcon as={ArrowUpDownDefaultIcon} />
        )}
      </ColumnHeaderWrapper>
    ),
    [sortField, sortOrder]
  );

  const skeletonBodyTemplate = useCallback(() => <Skeleton width="100%" height="18px" />, []);

  const handleRowClick = useCallback(
    (e: DataTableRowClickEvent) => {
      if (loading) {
        return;
      }
      const row = e.data as TableRow;
      history.push(row.link);
    },
    [history, loading]
  );

  const nameBodyTemplate = useCallback(
    (row: TableRow) => (
      <NameCell>
        {row.name}
        <Tooltip position="titleEllipsed">{row.name}</Tooltip>
      </NameCell>
    ),
    []
  );

  const registrationNumberBodyTemplate = useCallback(
    (row: TableRow) => <RegNumberCell>{row.registrationNumber}</RegNumberCell>,
    []
  );

  const statusBodyTemplate = useCallback(
    (row: TableRow) => {
      if (row.itemType === DashboardItemType.CopyGroup && row.groupStatus !== undefined) {
        const label =
          row.groupStatus === EPDCopyGroupStatus.DeclinedCopyByLicenseeAdmin ? 'Declined publication' : row.groupStatus;
        return (
          <StatusBadgeGroup status={row.groupStatus}>
            <BadgeText>
              {label}
              <Tooltip position="titleEllipsed">{label}</Tooltip>
            </BadgeText>
          </StatusBadgeGroup>
        );
      }
      if (row.epdStatus !== undefined) {
        const label = t(`epdWizard.epdState.status.${row.epdStatus}`);
        return (
          <StatusBadgeEpd status={row.epdStatus}>
            <BadgeText>
              {label}
              <Tooltip position="titleEllipsed">{label}</Tooltip>
            </BadgeText>
          </StatusBadgeEpd>
        );
      }
      return <span>-</span>;
    },
    [t]
  );

  return (
    <StyledDataTable
      value={loading ? SKELETON_ROWS : rows}
      onRowClick={handleRowClick}
      sortField={sortField}
      sortOrder={sortOrder}
      onSort={handleSort}
      removableSort
    >
      <Column
        field="registrationNumber"
        header={columnHeader('Registration number', 'registrationNumber')}
        sortable
        style={{ width: 287, minWidth: 287, maxWidth: 287 }}
        body={loading ? skeletonBodyTemplate : registrationNumberBodyTemplate}
      />
      <Column
        field="name"
        header="EPD name"
        style={{ width: 324, minWidth: 324, maxWidth: 324 }}
        body={loading ? skeletonBodyTemplate : nameBodyTemplate}
      />
      <Column
        field="epdStatus"
        header="Status"
        style={{ width: 160, minWidth: 160, maxWidth: 160 }}
        body={loading ? skeletonBodyTemplate : statusBodyTemplate}
      />
      <Column
        field="versionDate"
        header={columnHeader('Version date', 'versionDate')}
        sortable
        style={{ width: 124, minWidth: 124, maxWidth: 124 }}
        body={loading ? skeletonBodyTemplate : undefined}
      />
      <Column
        field="validityDate"
        header={columnHeader('Validity date', 'validityDate')}
        sortable
        style={{ width: 141, minWidth: 141, maxWidth: 141 }}
        body={loading ? skeletonBodyTemplate : undefined}
      />
    </StyledDataTable>
  );
};

const StyledDataTable = styled(DataTable)`
  .p-datatable-thead > tr > th {
    background-color: ${(props) => props.theme.colors.elementsFilledInput};
    color: ${(props) => props.theme.colors.primaryBlack70};
    font-family: Inter;
    font-weight: 600;
    padding: 0 16px;
    height: 50px;
    border: none;
    white-space: nowrap;
  }

  .p-datatable-tbody > tr:first-child > td {
    border-top: none;
  }

  .p-datatable-tbody > tr:last-child > td {
    border-bottom: 1px solid #dee2e6;
  }

  .p-datatable-thead > tr > th.p-highlight,
  .p-datatable-thead > tr > th.p-sortable-column:hover,
  .p-datatable-thead > tr > th.p-sortable-column.p-highlight:hover,
  .p-datatable-thead > tr > th.p-sortable-column:focus {
    background-color: ${(props) => props.theme.colors.elementsFilledInput};
    color: ${(props) => props.theme.colors.primaryBlack70};
    box-shadow: none;
    outline: none;
  }

  .p-datatable-tbody > tr {
    cursor: pointer;

    > td {
      font-family: Inter;
      font-size: 14px;
      color: ${(props) => props.theme.colors.primaryBlack};
      padding: 0 16px;
      height: 50px;
      vertical-align: middle;
      overflow: hidden;
    }

    &:hover > td {
      background-color: #b4d0b64d;
    }
  }

  .p-column-title {
    font-size: 14px;
  }

  .p-sortable-column-icon {
    display: none;
  }
`;

const NameCell = styled.div`
  position: relative;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SortIcon = styled.svg<{ $active?: boolean }>`
  width: 16px;
  height: 16px;
  margin-left: 4px;
  flex-shrink: 0;
  vertical-align: middle;
  color: ${({ $active, theme }) => ($active ? theme.colors.primaryOrange : theme.colors.statusLabelGrayText)};
`;

const ColumnHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.primaryBlack70};
  pointer-events: none;
`;

const RegNumberCell = styled.div`
  position: relative;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BadgeText = styled.span`
  position: relative;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StatusBadgeEpd = styled(CardStatus)`
  max-width: 128px;
  overflow: hidden;
`;

const StatusBadgeGroup = styled(EPDGroupCardDashboardStatus)`
  max-width: 128px;
  overflow: hidden;
  margin-left: 0;
`;

export default DashboardListView;
